import AbstractGraph from "graphology-types";
import Sigma from "sigma";

/**
 * Simple Graham-scan convex hull for 2D points.
 */
function convexHull(points: { x: number; y: number }[]): { x: number; y: number }[] {
  if (points.length < 3) return points;

  // Sort by x, then y
  const sorted = [...points].sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));

  const cross = (o: any, a: any, b: any) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const lower: { x: number; y: number }[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }

  const upper: { x: number; y: number }[] = [];
  for (const p of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }

  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

/**
 * Compute bounding box of point set.
 */
function boundingBox(points: { x: number; y: number }[]) {
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
    width: Math.max(...xs) - Math.min(...xs),
    height: Math.max(...ys) - Math.min(...ys),
  };
}

/**
 * Rotate set of points around center (cx, cy).
 */
function rotatePoints(
  points: { x: number; y: number }[],
  rad: number,
  cx: number,
  cy: number
) {
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return points.map(p => ({
    x: (p.x - cx) * cos - (p.y - cy) * sin + cx,
    y: (p.x - cx) * sin + (p.y - cy) * cos + cy,
  }));
}

/**
 * Find best rotation angle (degrees) using convex hull analysis.
 */
export function computeBestRotationAngle(graph: AbstractGraph): number {
  const visibleNodes = graph
    .nodes()
    .map(key => ({
      x: graph.getNodeAttribute(key, "x") as number,
      y: graph.getNodeAttribute(key, "y") as number,
    }))
    .filter(Boolean);

  if (visibleNodes.length < 3) return 0;

  const hull = convexHull(visibleNodes);
  hull.push(hull[0]); // close polygon

  const xs = visibleNodes.map(p => p.x);
  const ys = visibleNodes.map(p => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

  let minHeight = Number.POSITIVE_INFINITY;
  let bestDeg = 0;

  for (let i = 1; i < hull.length; i++) {
    const p1 = hull[i - 1];
    const p2 = hull[i];
    const rad = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    const deg = (rad * 180) / Math.PI;

    const rotated = rotatePoints(visibleNodes, rad, cx, cy);
    const box = boundingBox(rotated);
    if (box.height < minHeight) {
      minHeight = box.height;
      bestDeg = deg;
    }
  }

  if (bestDeg > 90) bestDeg -= 180;
  else if (bestDeg < -90) bestDeg += 180;

  return bestDeg;
}

/**
 * Animate node rotation around centroid.
 */
export function rotateNodes(
  sigmaInstance: Sigma,
  degrees: number,
  seconds = 2,
  callback?: () => void
) {
  let animationId: number;
  let duration = seconds * 1000;

  const graph = sigmaInstance.getGraph();
  const nodes = graph.nodes();
  const radians = (degrees * Math.PI) / 180;
  const start = performance.now();

  const xs = nodes.map(n => graph.getNodeAttribute(n, "x") as number);
  const ys = nodes.map(n => graph.getNodeAttribute(n, "y") as number);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

  const positions = nodes.map(n => ({
    key: n,
    x: graph.getNodeAttribute(n, "x") as number,
    y: graph.getNodeAttribute(n, "y") as number,
  }));

  /**
   * One animation frame of the rotation
   */
  function frame(now: number) {
    const t = Math.min((now - start) / duration, 1);
    const angle = radians * t;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    for (const {key, x, y} of positions) {
      /* In case graph is cleared during rotation frame */
      if (graph.hasNode(key)) {
        const newX = cx + (x - cx) * cos - (y - cy) * sin;
        const newY = cy + (x - cx) * sin + (y - cy) * cos;
        graph.setNodeAttribute(key, "x", newX);
        graph.setNodeAttribute(key, "y", newY);
      }
    }

    sigmaInstance.refresh();
    if (t < 1) animationId = requestAnimationFrame(frame);
    else if (callback) callback();
  }

  animationId = requestAnimationFrame(frame);

  return () => {
    duration = 0; // make any current running frame stop by thinking it's done
    cancelAnimationFrame(animationId);
  }
}

/**
 * Compute optimal rotation and animate.
 */
export function rotateGraphSmart(sigmaInstance: Sigma, seconds = 3) {
  const bestDeg = computeBestRotationAngle(sigmaInstance.getGraph());
  const cancel = rotateNodes(sigmaInstance, bestDeg, seconds);
  return {bestDeg, cancel};
}
